import { useState } from 'react';
import {
  useExtractGeoDesignMutation,
  useImportGeoDesignMutation,
  useLazyGetGeoDesignExportQuery,
  usePutGeoDesignMutation,
} from '@shared/api';
import {
  newElementId,
  type GeoColumnOrder,
  type GeoDesignElement,
  type GeoDesignExportFormat,
  type GeoElementKind,
  type GeoExtractBody,
  type GeoPileLevel,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { IconPlus } from '@shared/ui/icons';
import {
  ErrorText,
  Muted,
  Notice,
  Panel,
  PanelHead,
  PanelTitle,
  Row,
  Stack,
  downloadBlob,
  useAction,
  useDraft,
} from '@widgets/GeoSchemes';
import { AssemblePanel } from '../AssemblePanel';
import { useStepDirty, useWizard } from '../wizard';
import { CatalogImportSection } from './CatalogImportSection';
import { ElementsTable } from './ElementsTable';
import { ExportSection } from './ExportSection';
import { SheetExtractSection } from './SheetExtractSection';
import { PAGE, rowValid } from './constants';

/** Карточка «Проектные элементы (РД)»: каталог, извлечение из листа, ручная правка. */
export function DesignPanel() {
  const { scheme, readOnly, goTo } = useWizard();
  const els = useDraft(scheme.design.elements);
  useStepDirty('design', els.dirty && !readOnly);
  const [put] = usePutGeoDesignMutation();
  const [importDesign] = useImportGeoDesignMutation();
  const [extract] = useExtractGeoDesignMutation();
  const [exportDesign] = useLazyGetGeoDesignExportQuery();
  const saving = useAction();
  const importing = useAction();
  const extracting = useAction();
  const exporting = useAction();

  const [order, setOrder] = useState<GeoColumnOrder>('nxyhc');
  const [editId, setEditId] = useState<string | null>(null);
  const [row, setRow] = useState<GeoDesignElement | null>(null);
  const [newIds, setNewIds] = useState<ReadonlySet<string>>(new Set());
  const [limit, setLimit] = useState(PAGE);

  const sheet = scheme.sheet;
  const sheetBound =
    sheet != null &&
    scheme.settings.binding.mode === 'sheet' &&
    scheme.settings.binding.control_points.length >= 2;
  const [layers, setLayers] = useState<string[]>([]);
  const [blocks, setBlocks] = useState<string[]>([]);
  const [circles, setCircles] = useState(true);
  const [points, setPoints] = useState(false);
  const [kind, setKind] = useState<GeoElementKind>(
    scheme.work_type === 'piles' ? 'pile' : 'column',
  );
  // Сваи PDF: пусто — сечение из таблицы свай листа, иначе по размеру знака.
  const [sizeMm, setSizeMm] = useState<number | null>(
    scheme.sheet?.kind === 'pdf' ? null : scheme.settings.work.element_size_mm,
  );
  const [pileLevel, setPileLevel] = useState<GeoPileLevel>('head_cut');
  // Бетон (КЖ) снимают по граням — с листа нужны контуры конструкций, а не точки.
  const [what, setWhat] = useState<'elements' | 'outlines'>(
    scheme.work_type === 'kzh' ? 'outlines' : 'elements',
  );
  const [hTop, setHTop] = useState<number | null>(null);
  const [hToContour, setHToContour] = useState(true);
  const surveyCount = scheme.survey.points.length;

  const list = els.draft;
  const shown = list.slice(0, limit);

  function confirmDiscard(): boolean {
    return (
      !els.dirty ||
      window.confirm('Есть несохранённые правки элементов — они пропадут. Продолжить?')
    );
  }

  function onImport(file: File) {
    if (!confirmDiscard()) return;
    void importing
      .run(
        () => importDesign({ id: scheme.id, file, column_order: order }).unwrap(),
        'Каталог не загрузился.',
      )
      .then((ok) => ok && els.reset());
  }

  function onExtract() {
    if (!sheet || !confirmDiscard()) return;
    const body: GeoExtractBody =
      what === 'outlines'
        ? { mode: 'outlines', h_top: hTop }
        : sheet.kind === 'pdf'
          ? { mode: 'piles_pdf', kind: 'pile', size_mm: sizeMm, pile_level: pileLevel }
          : { mode: 'dxf_entities', layers, blocks, circles, points, kind, size_mm: sizeMm };
    void extracting
      .run(
        () => extract({ id: scheme.id, ...body }).unwrap(),
        what === 'outlines' ? 'Контуры не найдены.' : 'Элементы не извлечены.',
      )
      .then((ok) => ok && els.reset());
  }

  function onExport(format: GeoDesignExportFormat) {
    void exporting.run(async () => {
      const blob = await exportDesign({ id: scheme.id, format }, false).unwrap();
      const drawing = format === 'dxf' || format === 'dwg';
      const what = !drawing
        ? 'вынос'
        : scheme.design.elements.length > 0
          ? 'оцифровка'
          : 'лист_в_МСК';
      downloadBlob(blob, `${scheme.number}_${what}.${format}`);
    }, 'Файл не выгрузился.');
  }

  function startEdit(el: GeoDesignElement) {
    setEditId(el.id);
    setRow(el);
  }

  function commitRow() {
    if (!row || !rowValid(row)) return;
    // Верх конструкции обычно ровный: новая отметка угла — всем углам контура.
    const before = list.find((x) => x.id === row.id);
    const spreadH = row.contour != null && hToContour && before != null && before.h !== row.h;
    els.update((prev) =>
      prev.map((x) =>
        x.id === row.id
          ? { ...row, label: row.label.trim() }
          : spreadH && x.contour === row.contour
            ? { ...x, h: row.h }
            : x,
      ),
    );
    setEditId(null);
    setRow(null);
  }

  function cancelRow() {
    if (editId && newIds.has(editId)) {
      els.update((prev) => prev.filter((x) => x.id !== editId));
    }
    setEditId(null);
    setRow(null);
  }

  function addRow() {
    const el: GeoDesignElement = {
      id: newElementId(),
      label: '',
      kind: scheme.work_type === 'piles' ? 'pile' : 'point',
      x: Number.NaN,
      y: Number.NaN,
      h: null,
      size_mm: scheme.settings.work.element_size_mm,
      source: 'manual',
    };
    els.update((prev) => [el, ...prev]);
    setNewIds((s) => new Set([...s, el.id]));
    startEdit(el);
  }

  async function save() {
    if (editId) return;
    const ok = await saving.run(
      () => put({ id: scheme.id, elements: list.filter(rowValid) }).unwrap(),
      'Элементы не сохранены.',
    );
    if (ok) {
      els.reset();
      setNewIds(new Set());
    }
  }

  const invalidCount = list.filter((x) => !rowValid(x)).length;
  // Сваи без номера на листе — «б/н 1»: номер вписывают сами, отметка — из таблицы листа.
  const nameless = list.filter((x) => x.kind === 'pile' && x.label.startsWith('б/н')).length;

  return (
    <Panel aria-labelledby="geo-design-title">
      <PanelHead>
        <PanelTitle id="geo-design-title">Проектные элементы (РД)</PanelTitle>
        <Muted>
          {list.length} шт.{scheme.design.file_name ? ` · ${scheme.design.file_name}` : ''}
        </Muted>
      </PanelHead>
      <Stack $gap={4}>
        {scheme.design_stale && (
          <Notice $tone="danger" role="alert">
            <div>{scheme.design_stale}.</div>
            <Muted>
              Пока проект не взят заново, сличение идёт с прежними элементами — а с ними точки
              съёмки могут не найти пары. Ниже: «Собрать из листов» (листы комплекта) или «Извлечь
              с листа».
            </Muted>
          </Notice>
        )}
        <AssemblePanel confirmDiscard={confirmDiscard} />
        {!readOnly && (
          <CatalogImportSection
            order={order}
            setOrder={setOrder}
            busy={importing.busy}
            error={importing.error}
            onImport={onImport}
          />
        )}

        {!readOnly && sheet && (
          <SheetExtractSection
            sheet={sheet}
            sheetBound={sheetBound}
            goTo={goTo}
            what={what}
            setWhat={setWhat}
            surveyCount={surveyCount}
            hTop={hTop}
            setHTop={setHTop}
            pileLevel={pileLevel}
            setPileLevel={setPileLevel}
            layers={layers}
            setLayers={setLayers}
            blocks={blocks}
            setBlocks={setBlocks}
            circles={circles}
            setCircles={setCircles}
            points={points}
            setPoints={setPoints}
            kind={kind}
            setKind={setKind}
            sizeMm={sizeMm}
            setSizeMm={setSizeMm}
            busy={extracting.busy}
            error={extracting.error}
            onExtract={onExtract}
          />
        )}

        {scheme.design.warnings.length > 0 && (
          <Notice $tone="warning" role="status">
            {scheme.design.warnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </Notice>
        )}

        {nameless > 0 && scheme.design.pile_table && !readOnly && (
          <Notice $tone="info" role="status">
            Свай без номера на листе: {nameless} («б/н …»). Впишите номер в колонку «Метка» и
            сохраните — отметки, марку и сечение программа возьмёт из таблицы «
            {scheme.design.pile_table.title}».
          </Notice>
        )}

        <ExportSection
          elementsCount={scheme.design.elements.length}
          sheetBound={sheetBound}
          sheetKind={sheet?.kind}
          dirty={els.dirty}
          busy={exporting.busy}
          error={exporting.error}
          onExport={onExport}
        />

        <Row $gap={2}>
          {!readOnly && (
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={addRow}
              disabled={editId != null}
            >
              <IconPlus size={16} /> Добавить элемент
            </Button>
          )}
          {els.dirty && !readOnly && (
            <>
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                onClick={() => void save()}
                disabled={saving.busy || editId != null}
              >
                {saving.busy ? 'Сохранение…' : 'Сохранить элементы'}
              </Button>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => {
                  els.reset();
                  setEditId(null);
                  setRow(null);
                  setNewIds(new Set());
                }}
                disabled={saving.busy}
              >
                Отменить правки
              </Button>
            </>
          )}
          {invalidCount > 0 && (
            <Muted>Без метки или координат: {invalidCount} — не сохранятся.</Muted>
          )}
        </Row>
        {saving.error && <ErrorText role="alert">{saving.error}</ErrorText>}

        <ElementsTable
          list={list}
          shown={shown}
          limit={limit}
          setLimit={setLimit}
          readOnly={readOnly}
          editId={editId}
          row={row}
          setRow={setRow}
          hToContour={hToContour}
          setHToContour={setHToContour}
          startEdit={startEdit}
          commitRow={commitRow}
          cancelRow={cancelRow}
          onDelete={(id) => els.update((prev) => prev.filter((x) => x.id !== id))}
        />
      </Stack>
    </Panel>
  );
}
